import classNames from 'classnames';
import React, { CSSProperties, FC, PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';

import './sporsmalside-animation.scss';

enum AnimationStateTypes {
    Invisible = 'Invisible',
    AnimatingInBegin = 'AnimatingInBegin',
    AnimatingInEnd = 'AnimatingInEnd',
    Visible = 'Visible',
    AnimatingOutBegin = 'AnimatingOutBegin',
    AnimatingOutEnd = 'AnimatingOutEnd',
}

type AnimationState =
    | { type: AnimationStateTypes.Invisible }
    | { type: AnimationStateTypes.AnimatingInBegin }
    | { type: AnimationStateTypes.AnimatingInEnd; height: number }
    | { type: AnimationStateTypes.Visible }
    | { type: AnimationStateTypes.AnimatingOutBegin; height: number }
    | { type: AnimationStateTypes.AnimatingOutEnd };

const invisible = (): AnimationState => ({ type: AnimationStateTypes.Invisible });

const animatingInBegin = (): AnimationState => ({ type: AnimationStateTypes.AnimatingInBegin });

const animatingInEnd = (height: number): AnimationState => ({ type: AnimationStateTypes.AnimatingInEnd, height });

const visible = (): AnimationState => ({ type: AnimationStateTypes.Visible });

const animatingOutBegin = (height: number): AnimationState => ({ type: AnimationStateTypes.AnimatingOutBegin, height });

const animatingOutEnd = (): AnimationState => ({ type: AnimationStateTypes.AnimatingOutEnd });

const animationLengthInMillis = 300;

const styleProps = (animationState: AnimationState): CSSProperties | undefined => {
    if (animationState.type === AnimationStateTypes.AnimatingInBegin) {
        return { height: 0 };
    }
    if (animationState.type === AnimationStateTypes.AnimatingInEnd) {
        return { height: animationState.height };
    }
    if (animationState.type === AnimationStateTypes.AnimatingOutBegin) {
        return { height: animationState.height };
    }
    if (animationState.type === AnimationStateTypes.AnimatingOutEnd) {
        return { height: 0 };
    }
    return undefined;
};

const animationClassName = (animationState: AnimationState) => {
    switch (animationState.type) {
        case AnimationStateTypes.AnimatingInBegin:
            return 'sporsmal-animation__animating-in-begin';
        case AnimationStateTypes.AnimatingInEnd:
            return 'sporsmal-animation__animating-in-end';
        case AnimationStateTypes.Visible:
            return 'sporsmal-animation__visible';
        case AnimationStateTypes.AnimatingOutBegin:
            return 'sporsmal-animation__animating-out-begin';
        case AnimationStateTypes.AnimatingOutEnd:
            return 'sporsmal-animation__animating-out-end';
        default:
            return '';
    }
};

const initialAnimationState = (show: boolean, animate: boolean): AnimationState => {
    if (show && animate) {
        return animatingInBegin();
    }
    if (show && !animate) {
        return visible();
    }
    return invisible();
};

interface Props {
    show: boolean;
    animate: boolean;
}

export const SporsmalAnimation: FC<PropsWithChildren<Props>> = ({ show, animate, children }) => {
    const ref = useRef<HTMLDivElement>();
    const [animationState, setAnimationState] = useState<AnimationState>(initialAnimationState(show, animate));

    useEffect(() => {
        if (
            show &&
            animationState.type !== AnimationStateTypes.Visible &&
            animationState.type !== AnimationStateTypes.AnimatingInBegin &&
            animationState.type !== AnimationStateTypes.AnimatingInEnd
        ) {
            setAnimationState(animatingInBegin());
        } else if (
            !show &&
            ref.current &&
            animationState.type !== AnimationStateTypes.Invisible &&
            animationState.type !== AnimationStateTypes.AnimatingOutBegin &&
            animationState.type !== AnimationStateTypes.AnimatingOutEnd
        ) {
            setAnimationState(animatingOutBegin(ref.current.scrollHeight));
        }
    }, [show, animationState]);

    const setAnimationStateWithTimeout = useCallback(
        (animationState: AnimationState, delayInMillis: number): (() => void) => {
            const timer = setTimeout(() => {
                setAnimationState(animationState);
            }, delayInMillis);
            return () => {
                clearTimeout(timer);
            };
        },
        [setAnimationState],
    );

    // eslint-disable-next-line consistent-return
    useEffect(() => {
        if (animationState.type === AnimationStateTypes.AnimatingInBegin && ref.current) {
            return setAnimationStateWithTimeout(animatingInEnd(ref.current.scrollHeight), 0);
        }
        if (animationState.type === AnimationStateTypes.AnimatingInEnd) {
            return setAnimationStateWithTimeout(visible(), animationLengthInMillis);
        }
        if (animationState.type === AnimationStateTypes.AnimatingOutBegin) {
            return setAnimationStateWithTimeout(animatingOutEnd(), 0);
        }
        if (animationState.type === AnimationStateTypes.AnimatingOutEnd) {
            return setAnimationStateWithTimeout(invisible(), animationLengthInMillis);
        }
    }, [animationState, setAnimationStateWithTimeout]);

    if (animationState.type === AnimationStateTypes.Invisible) {
        return null;
    }

    return (
        <div
            className={classNames('sporsmal-animation', animationClassName(animationState))}
            ref={ref}
            style={styleProps(animationState)}
        >
            {children}
        </div>
    );
};
